import { Copy, Download, Pencil, Plus, QrCode } from "lucide-react";
import { Link } from "@/lib/nav";
import { useState, useTransition } from "react";
import { toast } from "sonner";

import Banner from "@buildpy/ui/banner";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import StatusBadge from "@buildpy/ui/status-badge";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { Input } from "@buildpy/ui/input";
import { Radio } from "@buildpy/ui/radio";
import { SimpleSelect } from "@buildpy/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@buildpy/ui/sheet";
import { settleRedirect } from "@/lib/redirect-error";
import type { PublicPageActionResult } from "@buildpy/core/modules/public-page/action-types";
import { createPublicPage, setPublicPageActive, updatePublicPage } from "@/api/actions/public-page";
import { publicPageQrFileName } from "@buildpy/core/modules/public-page/rules";
import {
  MAX_FEATURED_PORTFOLIOS,
  MAX_PUBLIC_PAGE_NAME,
  PUBLIC_PAGE_LAYOUT_LABEL,
  PUBLIC_PAGE_LAYOUTS,
  type PublicPageLayout,
} from "@buildpy/core/modules/public-page/types";
import { QrImage } from "@/modules/qr/qr-image";

export interface PublicPageLinkView {
  id: string;
  code: string;
  name: string;
  layout: PublicPageLayout;
  active: boolean;
  hero_portfolio_id: string | null;
  featured_ids: string[];
  url: string;
  // 이 링크로 들어온 상담 문의(유입 PAGE) 수.
  inquiries: number;
  // 켜진 링크만 QR 을 만든다.
  svg: string | null;
}

export interface PublicPagePortfolioOption {
  id: string;
  title: string;
  published: boolean;
}

interface Props {
  links: PublicPageLinkView[];
  portfolios: PublicPagePortfolioOption[];
  canEdit: boolean;
  // 조직 설정 `portfolio.public_page` — 꺼져 있으면 모든 링크가 404 다.
  publicEnabled: boolean;
}

interface Draft {
  id: string | null;
  name: string;
  layout: PublicPageLayout;
  hero: string;
  featured: string[];
}

const EMPTY_DRAFT: Draft = { id: null, name: "", layout: "GRID", hero: "", featured: [] };

// 지워진 사례가 배열에 남아 있으면 체크를 풀 수 없다 — 살아 있는 사례만 들고 연다.
export const draftOf = (link: PublicPageLinkView, live: ReadonlySet<string>): Draft => ({
  id: link.id,
  name: link.name,
  layout: link.layout,
  hero: link.hero_portfolio_id && live.has(link.hero_portfolio_id) ? link.hero_portfolio_id : "",
  featured: link.featured_ids.filter((id) => live.has(id)),
});

const titleOf = (p: PublicPagePortfolioOption): string => (p.published ? p.title : `${p.title} (비공개)`);

// 시공사례 「공개 링크」 — 링크마다 주소·레이아웃 · 복사·QR 은 누구나 · 만들기·고치기·끄기는 대표만.
const PublicPageLinks = ({ links, portfolios, canEdit, publicEnabled }: Props) => {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [qrOpen, setQrOpen] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  const run = (fallback: string, action: () => Promise<PublicPageActionResult>, done: string, after?: () => void) =>
    startTransition(async () => {
      try {
        const result = await action();
        if (!result.ok) {
          toast.error(result.message);
          return;
        }
        toast.success(done);
        after?.();
      } catch (caught) {
        if (settleRedirect(caught)) return;
        toast.error(fallback);
      }
    });

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("공개 링크 주소를 복사했습니다");
    } catch {
      toast.error("복사하지 못했습니다");
    }
  };

  const download = (link: PublicPageLinkView) => {
    if (!link.svg) return;
    const href = URL.createObjectURL(new Blob([link.svg], { type: "image/svg+xml" }));
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = publicPageQrFileName(link.code);
    anchor.click();
    // click 직후 해제하면 일부 브라우저가 내려받기를 시작하기 전에 주소가 사라진다.
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  };

  const toggle = (link: PublicPageLinkView) =>
    run(
      "저장하지 못했습니다. 잠시 후 다시 시도하세요.",
      () => setPublicPageActive({ id: link.id, active: !link.active }),
      link.active ? "링크를 껐습니다 — 주소가 닫힙니다" : "링크를 다시 켰습니다"
    );

  const save = () => {
    if (!draft) return;
    const input = {
      name: draft.name,
      layout: draft.layout,
      hero_portfolio_id: draft.hero || null,
      featured_ids: draft.layout === "STORY" ? draft.featured : [],
    };
    run(
      "저장하지 못했습니다. 잠시 후 다시 시도하세요.",
      () => (draft.id ? updatePublicPage({ ...input, id: draft.id }) : createPublicPage(input)),
      draft.id ? "공개 링크를 고쳤습니다" : "공개 링크를 만들었습니다",
      () => setDraft(null)
    );
  };

  const toggleFeatured = (id: string, on: boolean) =>
    setDraft((d) => (d ? { ...d, featured: on ? [...d.featured, id] : d.featured.filter((f) => f !== id) } : d));

  const heroOptions = [{ value: "", label: "고르지 않음" }, ...portfolios.map((p) => ({ value: p.id, label: titleOf(p) }))];

  return (
    <SectionCard
      actions={
        canEdit && (
          <Button data-testid="public-page-new" size="sm" type="button" variant="outline" onClick={() => setDraft(EMPTY_DRAFT)}>
            <Plus className="size-4" />
            링크 만들기
          </Button>
        )
      }
      className="mb-4"
      data-testid="public-page-links"
      description="광고·블로그·명함마다 주소를 따로 만들어 붙입니다. 끄면 그 주소만 닫힙니다."
      title="공개 링크">
      {!publicEnabled && (
        <Banner className="mb-3" tone="warning">
          <span data-testid="public-page-off">공개 페이지가 꺼져 있어 링크가 열리지 않습니다. </span>
          <Link className="underline underline-offset-2" href="/app/settings">
            설정에서 켜기
          </Link>
        </Banner>
      )}

      {links.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="public-page-empty">
          {canEdit ? "아직 만든 공개 링크가 없습니다." : "아직 만든 공개 링크가 없습니다. 대표가 만들 수 있습니다."}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border" data-testid="public-page-list">
          {links.map((link) => (
            <li key={link.id} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0" data-testid="public-page-row">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{link.name}</span>
                <Pill>{PUBLIC_PAGE_LAYOUT_LABEL[link.layout]}</Pill>
                <StatusBadge label={link.active ? "켜짐" : "꺼짐"} tone={link.active ? "done" : "dropped"} />
                <span className="text-xs text-muted-foreground" data-testid="public-page-inquiries">
                  문의 {link.inquiries}건
                </span>
              </div>
              <p className="break-all text-xs text-muted-foreground" data-testid="public-page-url">
                {link.url}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" type="button" variant="outline" onClick={() => copy(link.url)}>
                  <Copy className="size-4" />
                  주소 복사
                </Button>
                {link.svg && (
                  <Button
                    aria-expanded={qrOpen === link.id}
                    size="sm"
                    type="button"
                    variant="outline"
                    onClick={() => setQrOpen((open) => (open === link.id ? null : link.id))}>
                    <QrCode className="size-4" />
                    QR
                  </Button>
                )}
                {canEdit && (
                  <>
                    <Button disabled={pending} size="sm" type="button" variant="outline" onClick={() => setDraft(draftOf(link, new Set(portfolios.map((p) => p.id))))}>
                      <Pencil className="size-4" />
                      고치기
                    </Button>
                    <Button
                      data-testid="public-page-toggle"
                      disabled={pending}
                      size="sm"
                      type="button"
                      variant="ghost"
                      onClick={() => toggle(link)}>
                      {link.active ? "끄기" : "다시 켜기"}
                    </Button>
                  </>
                )}
              </div>
              {link.svg && qrOpen === link.id && (
                <div className="flex flex-wrap items-end gap-3">
                  <QrImage alt={`${link.name} QR`} className="size-32 shrink-0 rounded-md border" svg={link.svg} />
                  <Button size="sm" type="button" variant="outline" onClick={() => download(link)}>
                    <Download className="size-4" />
                    QR 내려받기
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <Sheet open={draft !== null} onOpenChange={(open) => !open && setDraft(null)}>
          <SheetContent isClose className="flex w-full flex-col gap-y-4 overflow-y-auto p-4 sm:max-w-md">
            <SheetHeader className="flex-col items-start gap-y-1 p-0 pr-8">
              <SheetTitle>{draft?.id ? "공개 링크 고치기" : "공개 링크 만들기"}</SheetTitle>
              <SheetDescription className="text-xs">
                주소는 만들 때 정해지고 바뀌지 않습니다.
              </SheetDescription>
            </SheetHeader>
            {draft && (
              <>
                <Input
                  id="public-page-name"
                  label="이름"
                  maxLength={MAX_PUBLIC_PAGE_NAME}
                  name="public-page-name"
                  placeholder="예: 네이버 블로그"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
                <fieldset className="flex flex-col gap-y-1.5">
                  <legend className="mb-1 text-xs text-muted-foreground">레이아웃</legend>
                  {PUBLIC_PAGE_LAYOUTS.map((layout) => (
                    <label key={layout} className="flex items-center gap-x-2 text-sm">
                      <Radio
                        checked={draft.layout === layout}
                        name="public-page-layout"
                        onChange={() => setDraft({ ...draft, layout })}
                      />
                      {PUBLIC_PAGE_LAYOUT_LABEL[layout]}
                    </label>
                  ))}
                </fieldset>
                <div className="flex flex-col gap-y-1.5">
                  <label className="text-xs text-muted-foreground" htmlFor="public-page-hero">
                    맨 위 대표 사례
                  </label>
                  <SimpleSelect
                    id="public-page-hero"
                    name="public-page-hero"
                    options={heroOptions}
                    value={draft.hero}
                    onValueChange={(hero) => setDraft({ ...draft, hero })}
                  />
                </div>
                {draft.layout === "STORY" && (
                  <fieldset className="flex flex-col gap-y-1.5">
                    <legend className="mb-1 text-xs text-muted-foreground">
                      이야기로 보여 줄 사례 ({draft.featured.length}/{MAX_FEATURED_PORTFOLIOS})
                    </legend>
                    {portfolios.length === 0 && <p className="text-sm text-muted-foreground">등록한 시공사례가 없습니다.</p>}
                    {portfolios.map((p) => {
                      const checked = draft.featured.includes(p.id);
                      return (
                        <label key={p.id} className="flex items-center gap-x-2 text-sm">
                          <Checkbox
                            checked={checked}
                            disabled={!checked && draft.featured.length >= MAX_FEATURED_PORTFOLIOS}
                            onCheckedChange={(on) => toggleFeatured(p.id, on === true)}
                          />
                          {titleOf(p)}
                        </label>
                      );
                    })}
                  </fieldset>
                )}
                <p className="text-xs text-muted-foreground">비공개 사례는 골라 두어도 공개 화면에 나오지 않습니다.</p>
                <Button data-testid="public-page-save" disabled={pending || draft.name.trim() === ""} type="button" onClick={save}>
                  {draft.id ? "저장" : "만들기"}
                </Button>
              </>
            )}
          </SheetContent>
        </Sheet>
      )}
    </SectionCard>
  );
};

export default PublicPageLinks;
